import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Grid,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import PvAdmissionTemplateSelect from '../../../../../PvAdmissionTemplateSelect'
import AutoCompleteInput from '../../../../../AutoCompleteInput'
import DatePicker from '../../../../../../components/DatePicker'
import { format } from 'date-fns'

export default function VehicleTypeDetailsStep({ data, onConfirm }) {
  const { t } = useTranslation()

  const [error, setError] = useState('')
  const [type, setType] = useState((data && data.type) || '')
  const [specificity, setSpecificity] = useState(
    (data && data.specificity) || ''
  )
  const [constructor, setConstructor] = useState(
    (data && data.constructor) || null
  )
  const [member, setMember] = useState((data && data.member) || null)
  const [frameworkAgreement, setFrameworkAgreement] = useState(
    (data && data.frameworkAgreement) || null
  )
  const [contractNumber, setContractNumber] = useState(
    (data && data.contractNumber) || null
  )
  const [lotNumber, setLotNumber] = useState((data && data.lotNumber) || null)
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState(
    (data && data.purchaseOrderNumber) || null
  )
  const [purchaseOrderEmissionDate, setPurchaseOrderEmissionDate] = useState(
    (data && data.purchaseOrderEmissionDate) || null
  )
  const [totalVehicles, setTotalVehicles] = useState(
    (data && data.totalVehicles) || null
  )
  const [excludeFromPvAdmission, setExcludeFromPvAdmission] = useState(
    (data && data.excludeFromPvAdmission) || false
  )
  const [pvAdmissionTemplateId, setPvAdmissionTemplateId] = useState(
    (data && data.pvAdmissionTemplateId) ?? null
  )

  const onConfirmData = () => {
    if (pvAdmissionTemplateId == null) {
      return
    }

    if (type.length === 0) {
      setError('type_required')
      return
    }

    if (specificity.length === 0) {
      setError('specificity_required')
      return
    }

    const newData = !constructor
      ? {
          type,
          specificity,
        }
      : {
          type,
          specificity,
          constructor,
        }
    if (member) {
      newData.member = member
    }
    if (frameworkAgreement) {
      newData.frameworkAgreement = frameworkAgreement
    }
    if (contractNumber) {
      newData.contractNumber = contractNumber
    }
    if (lotNumber) {
      newData.lotNumber = lotNumber
    }
    if (purchaseOrderNumber) {
      newData.purchaseOrderNumber = purchaseOrderNumber
    }
    if (purchaseOrderEmissionDate) {
      newData.purchaseOrderEmissionDate = format(
        purchaseOrderEmissionDate,
        'yyyy-MM-dd'
      )
    }
    if (totalVehicles) {
      newData.totalVehicles = totalVehicles
    }
    newData.excludeFromPvAdmission = excludeFromPvAdmission
    newData.pvAdmissionTemplateId = pvAdmissionTemplateId

    onConfirm(newData) // Pass data to parent
  }

  return (
    <Grid
      container
      direction="column"
      justifyContent="space-between"
      spacing={2}
    >
      <Grid item>
        <Typography variant="h5">Informations</Typography>
      </Grid>
      <Grid item style={{ display: 'flex' }} xs={6}>
        <TextField
          variant="standard"
          required
          error={error === 'type_required'}
          helperText={t('common.required')}
          id="type"
          label={t('create-vehicle-type-dialog.type')}
          fullWidth
          value={type}
          onChange={(event) => setType(event.target.value)}
        />
      </Grid>
      <Grid item style={{ display: 'flex' }} xs={6}>
        <TextField
          variant="standard"
          required
          error={error === 'specificity_required'}
          helperText={t('common.required')}
          id="specificity"
          label={t('create-vehicle-type-dialog.specificity')}
          fullWidth
          value={specificity}
          onChange={(event) => setSpecificity(event.target.value)}
        />
      </Grid>
      <Grid item xs={6}>
        <AutoCompleteInput
          onChange={setConstructor}
          label={t('create-vehicle-type-dialog.constructor-label')}
          resource="/constructors/autocomplete"
          value={constructor}
          canDelete
        />
      </Grid>
      <Grid item xs={6}>
        <AutoCompleteInput
          onChange={setMember}
          label={t('create-vehicle-type-dialog.member')}
          resource="vehicle-type/autocomplete-members"
          value={member}
          canDelete
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          variant="standard"
          id="frameworkAgreement"
          label={t('create-vehicle-type-dialog.framework-agreement')}
          fullWidth
          value={frameworkAgreement ?? ''}
          onChange={(event) => setFrameworkAgreement(event.target.value)}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          variant="standard"
          id="contractNumber"
          label={t('create-vehicle-type-dialog.contract-number')}
          fullWidth
          value={contractNumber ?? ''}
          onChange={(event) => setContractNumber(event.target.value)}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          variant="standard"
          id="lotNumber"
          label={t('create-vehicle-type-dialog.lot-number')}
          fullWidth
          value={lotNumber ?? ''}
          onChange={(event) => setLotNumber(event.target.value)}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          variant="standard"
          id="purchaseOrderNumber"
          label={t('create-vehicle-type-dialog.purchase-order-number')}
          fullWidth
          value={purchaseOrderNumber ?? ''}
          onChange={(event) => setPurchaseOrderNumber(event.target.value)}
        />
      </Grid>
      <Grid item xs={6}>
        <DatePicker
          id="purchaseOrderEmissionDate"
          value={purchaseOrderEmissionDate}
          onChange={setPurchaseOrderEmissionDate}
          label={t('create-vehicle-type-dialog.purchase-order-emission-date')}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          variant="standard"
          id="totalVehicles"
          label={t('create-vehicle-type-dialog.total-vehicle-number')}
          type="number"
          fullWidth
          value={totalVehicles}
          onChange={(event) => setTotalVehicles(event.target.value)}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={excludeFromPvAdmission}
              onChange={(event) =>
                setExcludeFromPvAdmission(event.target.checked)
              }
              name="excludeFromPvAdmission"
            />
          }
          label={t('create-vehicle-type-dialog.exclude-from-pv-admission')}
        />
      </Grid>
      <Grid item xs={12}>
        <PvAdmissionTemplateSelect
          value={pvAdmissionTemplateId}
          onChange={setPvAdmissionTemplateId}
          defaultToAcquisition
        />
      </Grid>
      <Grid item container justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          onClick={onConfirmData}
          disabled={pvAdmissionTemplateId == null}
        >
          Continuer
        </Button>
      </Grid>
    </Grid>
  )
}
